import React, { Component, useEffect, useRef, useState } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { ValidationError, ExpandingTextarea } from '../../components';
import css from './CustomDatePicker.module.css';
import DatePicker from 'react-datepicker';

const CONTENT_MAX_LENGTH = 5000;

function CustomDatePickerComponent(props) {
  const {
    rootClassName,
    className,
    inputRootClass,
    customErrorText,
    id,
    label,
    input,
    meta,
    onUnmount,
    isUncontrolled,
    inputRef,
    placeholder,
    minDate,
    subText,

    ...rest
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [isDateSelected, setIsDateSelected] = useState(false);
  const datePickerRef = useRef(null);

  /* eslint-enable no-unused-vars */
  if (typeof window === 'undefined') {
    return null;
  }

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error } = meta;
  const isTextarea = input.type === 'textarea';

  const errorText = customErrorText || error;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = !!customErrorText || !!(touched && invalid && error);

  const fieldMeta = { touched: hasError, error: errorText };

  // Textarea doesn't need type.
  const { type, ...inputWithoutType } = input;
  // Uncontrolled input uses defaultValue instead of value.
  const { value: defaultValue, ...inputWithoutValue } = input;
  // Use inputRef if it is passed as prop.
  const refMaybe = inputRef ? { ref: inputRef } : {};

  const inputClasses = inputRootClass || classNames(css.input);
  const inputSuccessClasses = inputRootClass || classNames(css.inputSuccess);
  const maxLength = CONTENT_MAX_LENGTH;
  const inputProps = isUncontrolled
    ? {
        className: inputClasses,
        id,
        type,
        defaultValue,
        // ...refMaybe,
        ...inputWithoutValue,
        ...rest,
      }
    : // ...refMaybe,
      { className: inputClasses, id, type, ...input, ...rest };
  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      {label ? <label htmlFor={id}>{label}</label> : null}
      {subText ? <div className={css.subtext}>{subText}</div> : null}
      <DatePicker
        {...inputProps}
        ref={datePickerRef}
        // onChange={input.onChange}
        onChange={date => {
          input.onChange(date); 
          setIsOpen(false);
        }}
        onFocus={e => {
          if (!isDateSelected) {
            setIsOpen(true);
          } else {
            setIsDateSelected(false);
          }
        }}
        onCalendarClose={() => {
          setIsDateSelected(true);
        }}
        open={isOpen}
        selected={input.value}
        placeholderText={placeholder}
        minDate={minDate}
        maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
        autoComplete="off"
        dateFormat="dd/MM/yyyy"
      />

      <ValidationError fieldMeta={fieldMeta} />
    </div>
  );
}

CustomDatePickerComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inputRootClass: null,
  onUnmount: null,
  customErrorText: null,
  id: null,
  label: null,
  isUncontrolled: false,
  inputRef: null,
};

CustomDatePickerComponent.propTypes = {
  rootClassName: string,
  className: string,
  inputRootClass: string,

  onUnmount: func,

  // Error message that can be manually passed to input field,
  // overrides default validation message
  customErrorText: string,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string,
  label: string,

  // Uncontrolled input uses defaultValue prop, but doesn't pass value from form to the field.
  // https://reactjs.org/docs/uncontrolled-components.html#default-values
  isUncontrolled: bool,
  // a ref object passed for input element.
  inputRef: object,

  // Generated by final-form's Field component
  input: shape({
    onChange: func.isRequired,
    // Either 'textarea' or something that is passed to the input element
    type: string.isRequired,
  }).isRequired,
  meta: object.isRequired,
};

class CustomDatePicker extends Component {
  componentWillUnmount() {
    // Unmounting happens too late if it is done inside Field component
    // (Then Form has already registered its (new) fields and
    // changing the value without corresponding field is prohibited in Final Form
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }

  render() {
    return <Field component={CustomDatePickerComponent} {...this.props} />;
  }
}

export default CustomDatePicker;
